import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Check, Save } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layout/AdminLayout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import {
  adminApproveImportReview,
  adminBulkUpdateImportReviewTeams,
  adminFetchImportReview,
  adminUpdateImportReviewTeam
} from '../services/competitionApi';
import {
  getCompetitionDisplayName
} from '../utils/competitionDisplayName';

const AdminCompetitionImportReview = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { language, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [approving, setApproving] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminFetchImportReview(competitionId);
      setCompetition(data.competition);
      setRows(
        (data.teams || []).map((t) => ({
          _id: t._id,
          nameHe: t.nameHe || '',
          nameEn: t.nameEn || '',
          logoUrl: t.logoUrl || '',
          importStatus: t.importStatus,
          externalTeamId: t.externalTeamId,
          dirty: false
        }))
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || (language === 'en' ? 'Failed to load' : 'טעינה נכשלה'));
    } finally {
      setLoading(false);
    }
  }, [competitionId, language]);

  useEffect(() => {
    load();
  }, [load]);

  const dirtyCount = useMemo(() => rows.filter((r) => r.dirty).length, [rows]);

  const allComplete = useMemo(
    () =>
      rows.length > 0 &&
      rows.every(
        (r) =>
          r.nameEn.trim() &&
          r.nameHe.trim() &&
          /[\u0590-\u05FF]/.test(r.nameHe)
      ),
    [rows]
  );

  const updateRow = (id, patch) => {
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, ...patch, dirty: true } : r))
    );
  };

  const saveRow = async (row, { silent } = {}) => {
    try {
      setSavingId(row._id);
      await adminUpdateImportReviewTeam(competitionId, row._id, {
        nameHe: row.nameHe.trim(),
        nameEn: row.nameEn.trim(),
        logoUrl: row.logoUrl.trim()
      });
      setRows((prev) =>
        prev.map((r) => (r._id === row._id ? { ...r, dirty: false } : r))
      );
      if (!silent) {
        toast.success(language === 'en' ? 'Team saved' : 'הקבוצה נשמרה');
      }
      return true;
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (language === 'en' ? 'Save failed' : 'שמירה נכשלה')
      );
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const saveAllDirty = async () => {
    const dirty = rows.filter((r) => r.dirty);
    if (!dirty.length) {
      toast(
        language === 'en' ? 'No changes to save' : 'אין שינויים לשמירה'
      );
      return;
    }
    try {
      setSavingAll(true);
      const data = await adminBulkUpdateImportReviewTeams(
        competitionId,
        dirty.map((r) => ({
          _id: r._id,
          nameHe: r.nameHe.trim(),
          nameEn: r.nameEn.trim(),
          logoUrl: r.logoUrl.trim()
        }))
      );
      const failedIds = new Set((data.failed || []).map((f) => String(f.teamId)));
      setRows((prev) =>
        prev.map((r) =>
          dirty.some((d) => d._id === r._id) && !failedIds.has(String(r._id))
            ? { ...r, dirty: false }
            : r
        )
      );
      if (data.failed?.length) {
        const first = data.failed[0];
        toast.error(
          `${first.nameHe || first.nameEn || first.teamId}: ${first.message}`
        );
      } else {
        toast.success(
          language === 'en'
            ? `Saved ${dirty.length} teams`
            : `${dirty.length} קבוצות נשמרו`
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (language === 'en' ? 'Save all failed' : 'שמירת הכל נכשלה')
      );
    } finally {
      setSavingAll(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      const dirty = rows.filter((r) => r.dirty);
      if (dirty.length) {
        const data = await adminBulkUpdateImportReviewTeams(
          competitionId,
          dirty.map((r) => ({
            _id: r._id,
            nameHe: r.nameHe.trim(),
            nameEn: r.nameEn.trim(),
            logoUrl: r.logoUrl.trim()
          }))
        );
        if (data.failed?.length) {
          toast.error(data.failed[0].message);
          return;
        }
      }
      await adminApproveImportReview(competitionId);
      toast.success(
        language === 'en' ? 'Import approved' : 'הייבוא אושר'
      );
      navigate('/admin/competitions');
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          (language === 'en' ? 'Approve failed' : 'האישור נכשל')
      );
    } finally {
      setApproving(false);
    }
  };

  const labelCls = `block text-xs font-semibold text-zinc-600 mb-1 ${isRTL ? 'text-right' : 'text-left'}`;
  const inputCls =
    'w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20';

  return (
    <AdminLayout
      title={language === 'en' ? 'Team import review' : 'אישור ייבוא קבוצות'}
      subtitle={
        competition
          ? getCompetitionDisplayName(competition, language)
          : language === 'en'
            ? 'Review names and logos, then approve'
            : 'בדקו שמות וסמלים ואז אשרו'
      }
      loading={loading}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/admin/competitions')}
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'en' ? 'Back' : 'חזרה'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={saveAllDirty}
            loading={savingAll}
            disabled={!dirtyCount}
          >
            <Save className="h-4 w-4" />
            {language === 'en' ? `Save all (${dirtyCount})` : `שמור הכל (${dirtyCount})`}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleApprove}
            loading={approving}
            disabled={!allComplete}
          >
            <Check className="h-4 w-4" />
            {language === 'en' ? 'Approve & accept' : 'אשר וקבל'}
          </Button>
        </div>
      }
    >
      {competition?.teamsImportStatus === 'approved' ? (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {language === 'en'
            ? 'This import is already approved. You can still edit team details.'
            : 'הייבוא כבר אושר. אפשר עדיין לערוך פרטי קבוצות.'}
        </div>
      ) : (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {language === 'en'
            ? 'Fill Hebrew names for every team (same clubs can keep the same name across seasons). Then Save all and Approve.'
            : 'מלאו שם עברי לכל קבוצה (אותן קבוצות יכולות לשמור על אותו שם בין עונות). אחר כך שמרו הכל ואשרו.'}
        </div>
      )}

      {!rows.length && !loading ? (
        <Card className="py-12 text-center text-zinc-500">
          {language === 'en'
            ? 'No teams yet. Run Sync on the competitions page first.'
            : 'אין קבוצות עדיין. הריצו סנכרון בעמוד התחרויות.'}
        </Card>
      ) : (
        <div className="flex flex-col gap-3 pb-24">
          {rows.map((row) => (
            <Card key={row._id} className="grid gap-3 sm:grid-cols-[64px_1fr_auto] sm:items-end">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-zinc-100">
                {row.logoUrl ? (
                  <img src={row.logoUrl} alt="" className="h-14 w-14 object-contain" />
                ) : (
                  <span className="text-xs text-zinc-400">—</span>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>
                    {language === 'en' ? 'Hebrew name' : 'שם בעברית'}
                  </label>
                  <input
                    className={inputCls}
                    dir="rtl"
                    value={row.nameHe}
                    onChange={(e) => updateRow(row._id, { nameHe: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {language === 'en' ? 'English name' : 'שם באנגלית'}
                  </label>
                  <input
                    className={inputCls}
                    dir="ltr"
                    value={row.nameEn}
                    onChange={(e) => updateRow(row._id, { nameEn: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    {language === 'en' ? 'Logo URL' : 'כתובת סמל'}
                  </label>
                  <input
                    className={inputCls}
                    dir="ltr"
                    value={row.logoUrl}
                    onChange={(e) => updateRow(row._id, { logoUrl: e.target.value })}
                  />
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => saveRow(row)}
                loading={savingId === row._id}
                disabled={!row.dirty || savingAll}
              >
                <Save className="h-4 w-4" />
                {language === 'en' ? 'Save' : 'שמור'}
              </Button>
            </Card>
          ))}
        </div>
      )}

      {dirtyCount > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white/95 px-4 py-3 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
            <p className="text-sm text-zinc-600">
              {language === 'en'
                ? `${dirtyCount} unsaved team(s)`
                : `${dirtyCount} קבוצות לא שמורות`}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={saveAllDirty}
              loading={savingAll}
            >
              <Save className="h-4 w-4" />
              {language === 'en' ? 'Save all' : 'שמור הכל'}
            </Button>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
};

export default AdminCompetitionImportReview;
